/// <reference types="@types/google.maps" />
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { WA_SESSION_STORAGE } from '@ng-web-apis/common';
import {
  GOOGLE_MAPS_STATIC_MARKER,
  GOOGLE_MAPS_STATIC_URL,
  GoogleMapsStaticMapRequest,
  GoogleStaticMapConfig,
} from '@rma/generic/maps/domain';
import { appendToRmaApiUrl } from '@rma/generic/ng/util';
import { httpParamMapper } from '@rma/public/generic/util/http';
import * as qs from 'qs';
import { map, Observable, of, tap } from 'rxjs';
import { GOOGLE_MAPS_API_KEY } from './google-maps.provider';

@Injectable({ providedIn: 'root' })
export class GoogleMapsStaticImageService {
  private readonly apiKey = inject(GOOGLE_MAPS_API_KEY, { optional: true });
  private readonly httpClient = inject(HttpClient);
  private readonly rmaApiUrl = inject(appendToRmaApiUrl);
  private readonly sessionStorage = inject<Storage>(WA_SESSION_STORAGE);

  public getStaticImageWithSignature(request: GoogleMapsStaticMapRequest): Observable<string> {
    if (request.cache && this.sessionStorage?.getItem) {
      const cache = this.sessionStorage.getItem(request.cache);
      if (cache) {
        return of(cache);
      }
    }

    const params = httpParamMapper({
      markerParameterStyle: `icon:${request.markerParameterStyle}`,
      enableZoom: request.enableZoom,
      showMarker: request.showMarker,
      showPointOfInterest: request.showPointOfInterest,
      dimensions: {
        width: request.dimensions.width,
        height: request.dimensions.height,
        zoom: request.dimensions.zoom,
        scale: request.dimensions.scale,
      },
      locations: request.locations,
    });

    return this.httpClient.get<{ url: string }>(this.rmaApiUrl('googlemap/url'), { params }).pipe(
      map(({ url }) => url),
      tap((url) => {
        if (request.cache) {
          this.sessionStorage.setItem(request.cache, url);
        }
      }),
    );
  }

  public getStaticImage(config: Partial<GoogleStaticMapConfig>, markers: { icon?: string; coords: google.maps.LatLngLiteral }[]): string {
    const width = config.width ?? 640;
    const height = config.height ?? 640;
    const { scale, zoom, showPoi } = config;

    const params = qs.stringify({
      scale: scale ?? 1,
      size: `${width}x${height}`,
      zoom: zoom ?? 17,
      markers: `icon:${markers[0]?.icon ?? GOOGLE_MAPS_STATIC_MARKER}|${markers.map((m) => `${m.coords.lat},${m.coords.lng}`)}`,
      key: this.apiKey ?? undefined,
    });

    return `${GOOGLE_MAPS_STATIC_URL}?${params}&style=feature:poi|visibility:${showPoi ?? 'off'}`;
  }
}
